import { useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { createProduct, updateProduct } from '@/Api';
import { LoadInformation } from '@/services/Loaders';
import { validateFileSize } from '@/services/Exporters';
import * as THREE from 'three';
import { isFileSizeError, isServerError, getUserFriendlyErrorMessage } from '@/utils/errorHandling';
import { Annotation, ModelViewerSettings } from '@/types/Annotation.model';
import type { IProduct, LinkShareMode } from '@/types/Product.model';
import type { ModelViewerPresentationSettings } from '@/constants/modelViewerPresentation';
import { presentationSettingsToModelViewerSettings } from '@/constants/modelViewerPresentation';
import { mergeModelViewerSettingsForUpdate } from '@/utils/modelViewerSettingsMerge';
import { resolveGlbBlob, type ProductSubmissionData } from '@/utils/productSubmit';

export type { ProductSubmissionData };

interface SuccessPopupData {
  productId: string;
  productName: string;
}

interface UseProductSubmissionProps {
  modelFile: File | null;
  modelPreviewUri: string;
  initialCameraOrbit: string;
  initialCameraTarget: string;
  initialCameraFieldOfView: string;
  nonGltfModel: THREE.Object3D | null;
  preparedGlb: Blob | null;
  id?: string;
  annotations?: Annotation[];
  /** When editing (id set), merge with this snapshot so partial saves keep annotations/camera */
  loadedModelViewerSettingsRef?: MutableRefObject<Partial<ModelViewerSettings>>;
  presentationSettings?: ModelViewerPresentationSettings;
  /** Edit flow: user chose a replacement GLB via the model uploader */
  modelReplacement?: boolean;
  /** After a successful update — e.g. reset dirty baseline */
  onUpdateSuccess?: (saved: IProduct, submitted: ProductSubmissionData) => void;
}

async function appendModelGlbFile(
  formData: FormData,
  modelFile: File,
  nonGltfModel: THREE.Object3D | null,
  preparedGlb: Blob | null,
  t: (key: string) => string
): Promise<boolean> {
  let blob: Blob;
  try {
    blob = await resolveGlbBlob(modelFile, preparedGlb, nonGltfModel);
  } catch {
    notifications.show({
      title: t('editProduct.system'),
      message: t('editProduct.modelPreparationError'),
      color: 'red',
    });
    return false;
  }

  if (!validateFileSize(blob)) {
    notifications.show({
      title: t('editProduct.system'),
      message: t('editProduct.fileSizeError'),
      color: 'red',
    });
    return false;
  }

  formData.append(
    'productFiles',
    new File(
      [blob],
      modelFile.name.replace(LoadInformation.getFileExtension(modelFile.name), '.glb'),
      { type: 'model/gltf-binary' }
    )
  );
  return true;
}

export function useProductSubmission({
  modelFile,
  modelPreviewUri,
  initialCameraOrbit,
  initialCameraTarget,
  initialCameraFieldOfView,
  nonGltfModel,
  preparedGlb,
  id,
  annotations = [],
  loadedModelViewerSettingsRef,
  presentationSettings,
  modelReplacement = false,
  onUpdateSuccess,
}: UseProductSubmissionProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successPopupData, setSuccessPopupData] = useState<SuccessPopupData | null>(null);

  const submitProduct = async (product: ProductSubmissionData) => {
    const productName = (product?.productName ?? '').trim();
    if (!product || !productName) {
      notifications.show({
        title: t('editProduct.system'),
        message: t('editProduct.productNameRequired'),
        color: 'red',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('productName', productName);
      if (product.productPage) formData.append('productPage', product.productPage);
      formData.append('articleNumber', product.articleNumber.trim());
      formData.append('isPublic', product.isPublic.toString());
      formData.append('descriptionHtml', product.descriptionHtml ?? '');
      formData.append('category', (product.category ?? '').trim());

      const linkMode: LinkShareMode = product.linkShareMode ?? 'open';
      formData.append('linkShareMode', String(linkMode));
      if (linkMode === 'password') {
        const hadPassword = Boolean(product.linkSharePasswordSet);
        const sendPassword = !id || !hadPassword || Boolean(product.updateLinkSharePassword);
        if (sendPassword) {
          const pwd = (product.linkSharePassword ?? '').trim();
          if (pwd.length >= 4) formData.append('linkSharePassword', pwd);
        }
      }

      if (product.metadata && typeof product.metadata === 'object') {
        try {
          formData.append('metadata', JSON.stringify(product.metadata));
        } catch (e) {
          console.error('Metadata serialize error', e);
        }
      }

      if (modelPreviewUri) {
        const imageBlob = await (await fetch(modelPreviewUri, { credentials: 'include' })).blob();
        const imageFile = new File([imageBlob], 'preview.png', { type: 'image/png' });
        formData.append('productFiles', imageFile);
      }

      if (id && loadedModelViewerSettingsRef) {
        const merged = mergeModelViewerSettingsForUpdate(
          loadedModelViewerSettingsRef.current,
          annotations,
          initialCameraOrbit,
          initialCameraTarget,
          initialCameraFieldOfView,
          presentationSettings
        );
        formData.append('modelViewerSettings', JSON.stringify(merged));
      } else if (modelPreviewUri) {
        const modelViewerSettings: ModelViewerSettings = {
          ...(presentationSettings
            ? presentationSettingsToModelViewerSettings(presentationSettings)
            : {}),
        };
        if (initialCameraOrbit) modelViewerSettings.initialCameraOrbit = initialCameraOrbit;
        if (initialCameraTarget) modelViewerSettings.initialCameraTarget = initialCameraTarget;
        if (initialCameraFieldOfView) {
          modelViewerSettings.initialCameraFieldOfView = initialCameraFieldOfView;
        }
        if (annotations && annotations.length > 0) {
          modelViewerSettings.annotations = annotations;
        }
        formData.append('modelViewerSettings', JSON.stringify(modelViewerSettings));
      }

      if (!id) {
        if (!modelFile || !modelPreviewUri) {
          notifications.show({
            title: t('editProduct.system'),
            message: t('editProduct.uploadModelAndPreview'),
          });
          return;
        }

        const ok = await appendModelGlbFile(formData, modelFile, nonGltfModel, preparedGlb, t);
        if (!ok) return;
      }

      if (id) formData.append('_id', id);

      if (id && modelReplacement && modelFile) {
        const ok = await appendModelGlbFile(formData, modelFile, nonGltfModel, preparedGlb, t);
        if (!ok) return;
      }

      const savedProduct = await (id ? updateProduct(formData) : createProduct(formData));

      if (id && savedProduct.modelViewerSettings && loadedModelViewerSettingsRef) {
        try {
          loadedModelViewerSettingsRef.current = JSON.parse(
            savedProduct.modelViewerSettings
          ) as ModelViewerSettings;
        } catch {
          /* keep ref */
        }
      }

      if (!id && savedProduct._id) {
        setSuccessPopupData({
          productId: savedProduct._id,
          productName: productName,
        });
      } else if (id) {
        onUpdateSuccess?.(savedProduct, product);
        notifications.show({
          title: t('editProduct.system'),
          message: t('editProduct.productUpdated', { name: productName }),
        });
      }
    } catch (error: unknown) {
      console.error('Product submission error:', error);

      const errorMessage = getUserFriendlyErrorMessage(error);
      const isFileSizeErr = isFileSizeError(errorMessage);
      const status =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { status?: number } }).response?.status
          : undefined;
      const isServerErr = isServerError(status);

      notifications.show({
        title: t('editProduct.system'),
        message: isFileSizeErr
          ? t('editProduct.fileSizeError')
          : isServerErr
            ? errorMessage
            : id
              ? t('editProduct.updateError', {
                  name: productName,
                  error: errorMessage,
                })
              : t('editProduct.createError', {
                  name: productName,
                  error: errorMessage,
                }),
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitProduct,
    isSubmitting,
    successPopupData,
    setSuccessPopupData,
  };
}
